/**
 * Navigation Icons
 * 
 * Icons used for navigation and wayfinding in the application.
 * These help users move between different sections and pages.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { getIconClasses } from '../iconUtils';

const iconPropTypes = {
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']),
  color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'warning', 'danger', 'neutral', 'white']),
  className: PropTypes.string,
};

/**
 * DashboardIcon - Briefcase icon for dashboard navigation
 */
export const DashboardIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
  </svg>
);

DashboardIcon.propTypes = iconPropTypes;

/**
 * MessageIcon - Chat bubble with dots for messages
 */
export const MessageIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

MessageIcon.propTypes = iconPropTypes;

/**
 * HomeIcon - House icon for home navigation
 */
export const HomeIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

HomeIcon.propTypes = iconPropTypes;

/**
 * ArrowLeftIcon - Left arrow for back navigation
 */
export const ArrowLeftIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

ArrowLeftIcon.propTypes = iconPropTypes;

/**
 * ArrowRightIcon - Right arrow for forward navigation
 */
export const ArrowRightIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

ArrowRightIcon.propTypes = iconPropTypes;
